import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiStar, FiZap, FiCrown, FiPhone } = FiIcons;

function Pricing() {
  const [isAnnual, setIsAnnual] = useState(false);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const plans = [
    {
      name: "14 Days",
      icon: FiStar,
      price: 5000,
      description: "Perfect for short-term events",
      features: [
        "Unlimited Photos & Videos",
        "1 event gallery",
        "Basic customization",
        "14-day storage",
        "Email support",
        "Mobile responsive",
        "Download all media"
      ],
      gradient: "from-teal-400 to-cyan-500",
      bgGradient: "from-teal-50 to-cyan-50",
      popular: false,
      isContactUs: false
    },
    {
      name: "Premium Plan",
      icon: FiZap,
      price: 10000,
      description: "Most popular for events",
      features: [
        "Unlimited photos & videos",
        "Up to 10 event galleries",
        "Advanced customization",
        "Lifetime storage",
        "Priority support",
        "Custom branding",
        "Analytics dashboard",
        "White-label solution",
        "API access"
      ],
      gradient: "from-cyan-400 to-blue-500",
      bgGradient: "from-cyan-50 to-blue-50",
      popular: true,
      isContactUs: false
    },
    {
      name: "Agency",
      icon: FiCrown,
      price: null,
      description: "For agencies and enterprises",
      features: [
        "Everything in Premium",
        "Unlimited galleries",
        "Multi-client management",
        "Advanced analytics",
        "24/7 phone support",
        "Custom domain",
        "Team collaboration",
        "Dedicated account manager",
        "Custom integrations"
      ],
      gradient: "from-rose-400 to-pink-500",
      bgGradient: "from-rose-50 to-pink-50",
      popular: false,
      isContactUs: true,
      whatsappLink: "https://wa.me/919059468240"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-rose-50 pt-16">
      {/* Hero Section */}
      <motion.section 
        className="py-20 px-4"
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 
            className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
            variants={fadeInUp}
          >
            Simple <span className="bg-gradient-to-r from-teal-500 via-cyan-500 to-rose-500 bg-clip-text text-transparent">Pricing</span>
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed mb-12"
            variants={fadeInUp}
          >
            Choose the perfect plan for your events. No hidden fees, cancel anytime.
          </motion.p>
        </div>
      </motion.section>

      {/* Pricing Cards */}
      <motion.section 
        className="pb-20 px-4"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div 
                key={index}
                className={`relative rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 ${
                  plan.popular ? 'ring-2 ring-teal-500 scale-105' : ''
                } bg-gradient-to-br ${plan.bgGradient}`}
                variants={fadeInUp}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-6 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center">
                  <div className={`bg-gradient-to-r ${plan.gradient} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                    <SafeIcon icon={plan.icon} className="text-2xl text-white" />
                  </div>

                  <h3 style={{
      color: 'rgb(17, 24, 39)'
    }} className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>

                  <div className="mb-8">
                    {plan.isContactUs ? (
                      <div className="text-3xl font-bold text-gray-900 mb-2">
                        Contact Us
                      </div>
                    ) : (
                      <div style={{
      color: 'rgb(17, 24, 39)'
    }} className="text-5xl font-bold text-gray-900 mb-2">
                        ₹{plan.price.toLocaleString('en-IN')}
                        <span className="text-lg text-gray-500 font-normal">
                          {plan.name === "Premium Plan" ? ' one-time' : ' for 14 days'}
                        </span>
                      </div>
                    )}
                  </div>

                  {plan.isContactUs ? (
                    <a 
                      href={plan.whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-full py-4 rounded-full font-semibold transition-all duration-200 mb-8 bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center space-x-2`}
                    >
                      <SafeIcon icon={FiPhone} className="text-lg" />
                      <span>Contact on WhatsApp</span>
                    </a>
                  ) : (
                    <a 
                      href="https://wa.me/919059468240"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-full py-4 rounded-full font-semibold transition-all duration-200 mb-8 ${
                        plan.popular 
                          ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg hover:shadow-xl hover:scale-105' 
                          : 'bg-white text-gray-900 border-2 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      Get Started
                    </a>
                  )}

                  <div className="text-left space-y-4">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <SafeIcon icon={FiCheck} className="text-teal-500 text-lg flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section 
        className="py-20 px-4 bg-white"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto">
          <motion.h2 
            className="text-4xl font-bold text-center text-gray-900 mb-16"
            variants={fadeInUp}
          >
            Frequently Asked Questions
          </motion.h2>

          <div className="space-y-8">
            {[
              {
                question: "Can I change my plan anytime?",
                answer: "Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any billing adjustments in INR."
              },
              {
                question: "What happens to my data after the 14-day plan expires?",
                answer: "Your galleries remain accessible for 7 days after expiration. During this time, you can download all your content or upgrade to a Premium plan for lifetime storage."
              },
              {
                question: "Is there a free trial?",
                answer: "Yes! All paid plans come with a 7-day free trial. No credit card required to start your trial."
              },
              {
                question: "How do I contact support for the Agency plan?",
                answer: "For Agency plan inquiries, you can reach us via WhatsApp or call our dedicated enterprise line. We'll schedule a consultation to understand your specific needs."
              },
              {
                question: "Do you accept Indian payment methods?",
                answer: "Yes! We accept all major Indian payment methods including UPI, Net Banking, Credit/Debit cards, and digital wallets. All transactions are processed securely in INR."
              }
            ].map((faq, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-2xl p-6"
                variants={fadeInUp}
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section 
        className="py-20 px-4 bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-600"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold text-white mb-8"
            variants={fadeInUp}
          >
            Ready to Get Started?
          </motion.h2>
          <motion.p 
            className="text-xl text-white/90 mb-8"
            variants={fadeInUp}
          >
            Join thousands of happy customers creating infinite memories
          </motion.p>
          <motion.a 
            href="https://wa.me/919059468240"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-gray-900 px-12 py-4 rounded-full text-lg font-semibold shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 transform inline-block"
            variants={fadeInUp}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Free Trial
          </motion.a>
        </div>
      </motion.section>
    </div>
  );
}

export default Pricing;